import React, {
	Children,
	cloneElement,
	isValidElement,
	ReactElement,
	useEffect,
	useRef,
	useState,
} from "react";
import BemIt, { addClass } from "@gtechdoodler/bem-it";
import ContentBox from "../../../feedback/ContextBox";
import { StyledWithInputOptions } from "./WithInputOptions.styles";
import { WithInputOptionsProps } from "./WithInputOptions.types";
import List from "../../../data/List";
import {
	ListEventNotifications,
	ListProps,
} from "../../../data/List/List.types";

const WithInputOptions: React.FC<WithInputOptionsProps> = ({
	className,
	children,
	showOptions,
	renderInput,
	onBlur,
	...props
}) => {
	const block = new BemIt("WithInputOptions");
	const inputContainerRef = useRef<HTMLDivElement>(null);
	const [eventNotifications, setEventNotifications] =
		useState<ListEventNotifications>({});

	const handleClick = (event: React.MouseEvent<HTMLElement>) => {
		event.stopPropagation();
	};

	const handleFocus = () => {
		inputContainerRef?.current?.scrollIntoView({ behavior: "smooth" });
	};

	const handleKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
		setEventNotifications({
			selected: event.key === "Enter",
			moveNext: event.key === "ArrowDown",
			moveBack: event.key === "ArrowUp",
		});
	};

	const renderChildren = () => {
		const childArray = Children.toArray(children);
		const singleChildList = childArray[0];
		if (
			childArray.length !== 1 ||
			!isValidElement(singleChildList) ||
			singleChildList.type !== List
		) {
			console.error(
				"Error: WithInputOptions should only render a single child List component."
			);
			return null;
		}
		return cloneElement(singleChildList as ReactElement<ListProps>, {
			eventNotifications,
			maxHeight: "252px",
			overflowY: "scroll",
		});
	};

	useEffect(() => {
		function handleBlur() {
			if (onBlur) onBlur();
		}

		document.addEventListener("click", handleBlur);
		return () => {
			document.removeEventListener("click", handleBlur);
		};
	}, [onBlur]);

	return (
		<StyledWithInputOptions
			ref={inputContainerRef}
			className={addClass(className).after(block)}
			onClick={handleClick}
			onKeyDown={handleKeyDown}
			onFocus={handleFocus}
		>
			{renderInput()}
			{showOptions && <ContentBox {...props}>{renderChildren()}</ContentBox>}
		</StyledWithInputOptions>
	);
};

export default WithInputOptions;
